import { z } from 'zod';
import {
  BaseResponseSchema,
  uuidSchema,
} from './primitives.js';
import {
  OpsDeviceCategorySchema,
  OpsDeviceDetailSchema,
} from './device-inventory.schemas.js';

// ─── Order Management ────────────────────────────────────────────────────────

export const OrderStatusSchema = z.enum(['draft', 'confirmed', 'shipped', 'completed', 'cancelled']);


export const OrderLineItemSchema = z.object({
  deviceCategory: OpsDeviceCategorySchema,
  sku: z.string().min(1).max(32),
  productName: z.string().min(1).max(128),
  quantity: z.number().int().min(0),
});




export const OrderSchema = z.object({
  id: uuidSchema,
  orderReference: z.string().min(1).max(64),
  externalReference: z.string().max(128).nullable(),
  tenantId: uuidSchema,
  status: OrderStatusSchema,
  orderDate: z.string(),
  expectedShipDate: z.string().nullable(),
  actualShipDate: z.string().nullable(),
  claimCode: z.string().regex(/^[A-Z0-9]{4}-[A-Z0-9]{4}$/).nullable().optional(),
  claimedAt: z.string().nullable().optional(),
  claimedByUserId: z.string().nullable().optional(),
  /** Resolved display label for claimedByUserId (user name or provider email). */
  claimedByName: z.string().nullable().optional(),
  notes: z.string().nullable(),
  lineItems: z.array(OrderLineItemSchema).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  tenantName: z.string().nullable().optional(),
  deviceCount: z.number().int().optional(),
});


export const CreateOrderRequestSchema = z.object({
  tenantId: uuidSchema,
  orderDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  externalReference: z.string().max(128).optional(),
  expectedShipDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  notes: z.string().max(2000).optional(),
  lineItems: z.array(OrderLineItemSchema).optional(),
});


export const UpdateOrderRequestSchema = z.object({
  status: OrderStatusSchema.optional(),
  externalReference: z.string().max(128).nullable().optional(),
  expectedShipDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  actualShipDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});


export const OrderListQuerySchema = z.object({
  tenantId: uuidSchema.optional(),
  status: OrderStatusSchema.optional(),
  search: z.string().max(100).trim().optional(),
  page: z.coerce.number().int().positive().optional().default(1),
  limit: z.coerce.number().int().positive().max(100).optional().default(20),
});


export const OrderReferenceParamSchema = z.object({
  orderReference: z.string().min(1).max(64),
});


export const OrdersForTenantQuerySchema = z.object({
  tenantId: uuidSchema,
});


export const OrderListResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    orders: z.array(OrderSchema),
    total: z.number().int(),
    page: z.number().int(),
    limit: z.number().int(),
  }),
});


export const OrderDetailResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    order: OrderSchema,
  }),
});


export const OrderCreateResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    order: OrderSchema,
  }),
});


export const OrderUpdateResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    order: OrderSchema,
  }),
});


export const ClaimOrderResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    order: OrderSchema,
    devicesClaimed: z.number().int(),
  }),
});


export const OrderDevicesResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    orderReference: z.string(),
    devices: z.array(OpsDeviceDetailSchema),
  }),
});


export const OrderInventoryStatusCountSchema = z.object({
  status: z.string().min(1).max(64),
  count: z.number().int().min(0),
});


export const OrderInventoryAvailabilitySchema = z.object({
  orderReference: z.string().min(1).max(64),
  totalDevices: z.number().int().min(0),
  byStatus: z.array(OrderInventoryStatusCountSchema),
  canConfirm: z.boolean(),
  canCancel: z.boolean(),
  canShip: z.boolean(),
  canClaimOnBehalf: z.boolean(),
});


export const OrderInventoryAvailabilityResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    availability: OrderInventoryAvailabilitySchema,
  }),
});


export const ConfirmOrderRequestSchema = z.object({
  idempotencyKey: z.string().min(8).max(128).optional(),
  notes: z.string().max(500).optional(),
});


export const ConfirmOrderResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    order: OrderSchema,
    devicesChecked: z.number().int().min(0),
  }),
});


export const CancelOrderRequestSchema = z.object({
  reason: z.string().min(1).max(500),
  idempotencyKey: z.string().min(8).max(128).optional(),
});


export const CancelOrderResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    order: OrderSchema,
    devicesReleased: z.number().int().min(0),
  }),
});


export const ShipOrderRequestSchema = z.object({
  notes: z.string().max(500).optional(),
  idempotencyKey: z.string().min(8).max(128).optional(),
});


export const ShipOrderResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    order: OrderSchema,
    devicesShipped: z.number().int().min(0),
  }),
});


export const ProviderClaimOrderOnBehalfRequestSchema = z.object({
  reason: z.string().min(1).max(500),
});


export const ProviderClaimOrderOnBehalfResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    order: OrderSchema,
    devicesClaimed: z.number().int().min(0),
  }),
});
// Inferred types
export type CancelOrderRequest = z.infer<typeof CancelOrderRequestSchema>;
export type CancelOrderResponse = z.infer<typeof CancelOrderResponseSchema>;
export type ClaimOrderResponse = z.infer<typeof ClaimOrderResponseSchema>;
export type ConfirmOrderRequest = z.infer<typeof ConfirmOrderRequestSchema>;
export type ConfirmOrderResponse = z.infer<typeof ConfirmOrderResponseSchema>;
export type CreateOrderRequest = z.infer<typeof CreateOrderRequestSchema>;
export type Order = z.infer<typeof OrderSchema>;
export type OrderCreateResponse = z.infer<typeof OrderCreateResponseSchema>;
export type OrderDetailResponse = z.infer<typeof OrderDetailResponseSchema>;
export type OrderDevicesResponse = z.infer<typeof OrderDevicesResponseSchema>;
export type OrderInventoryAvailability = z.infer<typeof OrderInventoryAvailabilitySchema>;
export type OrderInventoryAvailabilityResponse = z.infer<typeof OrderInventoryAvailabilityResponseSchema>;
export type OrderInventoryStatusCount = z.infer<typeof OrderInventoryStatusCountSchema>;
export type OrderLineItem = z.infer<typeof OrderLineItemSchema>;
export type OrderListQuery = z.infer<typeof OrderListQuerySchema>;
export type OrderListResponse = z.infer<typeof OrderListResponseSchema>;
export type OrderReferenceParam = z.infer<typeof OrderReferenceParamSchema>;
export type OrderStatus = z.infer<typeof OrderStatusSchema>;
export type OrderUpdateResponse = z.infer<typeof OrderUpdateResponseSchema>;
export type OrdersForTenantQuery = z.infer<typeof OrdersForTenantQuerySchema>;
export type ProviderClaimOrderOnBehalfRequest = z.infer<typeof ProviderClaimOrderOnBehalfRequestSchema>;
export type ProviderClaimOrderOnBehalfResponse = z.infer<typeof ProviderClaimOrderOnBehalfResponseSchema>;
export type ShipOrderRequest = z.infer<typeof ShipOrderRequestSchema>;
export type ShipOrderResponse = z.infer<typeof ShipOrderResponseSchema>;
export type UpdateOrderRequest = z.infer<typeof UpdateOrderRequestSchema>;
